// Copyright (c) Ping. All rights reserved.
//
// Embedded Persona identity-verification modal (web). Renders the
// `persona-react` <Inquiry> flow INSIDE the page (an iframe served from
// withpersona.com) so the user never leaves the app — no new tab, no popup
// window. Driven by an `inquiryId` already created server-side via
// `POST /v1/me/kyc`; the SDK resumes that inquiry. Completion / cancel /
// error fire callbacks so the Settings row can update in realtime.
//
// CSP: `frame-src`/`connect-src` allow `https://*.withpersona.com`
// (apps/web/vite.config.ts dev headers; prod mirrors at nginx/CDN). The page
// must NOT send `Cross-Origin-Embedder-Policy` or the iframe is blocked.

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// `persona-react` ships a webpack UMD bundle; Vite's CJS interop only
// reliably exposes the DEFAULT export, so the named `{ Inquiry }` import
// resolves to `undefined` at runtime ("Element type is invalid"). Use the
// default import (the `.d.ts` declares `export default Inquiry`).
import Inquiry from 'persona-react';

export interface PersonaKycModalProps {
  /** Inquiry id returned by POST /v1/me/kyc. */
  readonly inquiryId: string;
  /** Inquiry finished inside the embedded flow (status is Persona's). */
  readonly onComplete: (status: string) => void;
  /** User backed out of the flow. */
  readonly onCancel: () => void;
  /** SDK error (network, config, etc.). */
  readonly onError: (message: string) => void;
  /** Dismiss the modal chrome (close button / backdrop / Escape). */
  readonly onClose: () => void;
}

export function PersonaKycModal({
  inquiryId,
  onComplete,
  onCancel,
  onError,
  onClose,
}: PersonaKycModalProps): JSX.Element | null {
  // Close on Escape — mirrors the backdrop click.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  // Portal to <body> so this sits ABOVE the Settings modal (a react-native-
  // web <Modal> that also portals to body and creates a top-level stacking
  // context — an in-tree div, even at max z-index, stays beneath it).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Identity verification"
      className="fixed inset-0 z-[2147483646] flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => {
        // Backdrop click (outside the card) dismisses.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex h-[720px] max-h-[90vh] w-[480px] max-w-[94vw] flex-col overflow-hidden rounded-2xl shadow-2xl bg-light-surface-panel dark:bg-dark-surface-panel">
        <button
          type="button"
          aria-label="Close verification"
          onClick={onClose}
          className="absolute right-2.5 top-2.5 z-[1] flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 text-base leading-none text-light-text-secondary bg-black/5 hover:bg-black/10 dark:text-[#B5BAC3] dark:bg-white/10 dark:hover:bg-white/20"
        >
          ✕
        </button>
        {/* The Persona <Inquiry> renders its own iframe; the arbitrary
            `[&_iframe]:*` variants force it to fill the modal full-width. */}
        <div className="min-h-0 flex-1 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:border-0">
          <Inquiry
            inquiryId={inquiryId}
            frameWidth="100%"
            frameHeight="100%"
            onComplete={({ status }) => onComplete(status)}
            onCancel={() => onCancel()}
            onError={(err) => onError(err?.message ?? 'Verification error')}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
